
import React from 'react';
import { formatCurrency } from '../../utils/calculator';
import { InfoTooltip } from '../../ui/InfoTooltip';
import { ShieldCheckIcon, EuroIcon } from 'lucide-react';

interface TotalContributionProps {
  formData: any;
  results: any;
  weeklyPaymentWithLoan: number;
  actualWeeklyPayment: number;
  hasNursingHomeCost: boolean;
  isUsingLoan: boolean;
}

export function TotalContribution({ 
  formData, 
  results, 
  weeklyPaymentWithLoan,
  actualWeeklyPayment,
  hasNursingHomeCost,
  isUsingLoan 
}: TotalContributionProps) {
  const isCapped = hasNursingHomeCost && weeklyPaymentWithLoan > formData.nursingHomeCost;
  const stateSupport = hasNursingHomeCost ? Math.max(0, formData.nursingHomeCost - actualWeeklyPayment) : 0;
  
  return (
    <div className="bg-white rounded-lg border overflow-hidden shadow-sm">
      <div className="bg-grandpal-green/10 p-4 border-b">
        <h4 className="font-semibold text-grandpal-green">Contribution Breakdown</h4>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex justify-between items-center">
          <p className="font-medium">From Income</p>
          <p>{formatCurrency(results.incomeContribution.weekly)}/week</p>
        </div>
        
        <div className="flex justify-between items-center">
          <p className="font-medium">From Assets</p>
          <p>{formatCurrency(results.assetContribution.weekly)}/week</p>
        </div>
        
        <div className="border-t pt-4 flex justify-between items-center">
          <div className="flex items-center gap-1">
            <p className="font-medium">Total Calculated Contribution</p>
          </div>
          <p className={`font-semibold ${isCapped ? 'text-gray-500 line-through' : 'text-grandpal-green'}`}>
            {formatCurrency(results.weeklyContribution)}/week
          </p>
        </div>
        
        {isUsingLoan && (
          <>
            <div className="flex justify-between items-center text-amber-700">
              <p className="font-medium">Less: Deferred Amount</p>
              <p>- {formatCurrency(results.deferredAmount)}/week</p>
            </div>
            
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-1">
                <p className="font-medium">Contribution After Loan</p>
                <InfoTooltip content="This is your contribution after applying the Nursing Home Loan option." />
              </div>
              <p className={`font-semibold ${isCapped ? 'text-gray-500 line-through' : 'text-grandpal-green'}`}>
                {formatCurrency(weeklyPaymentWithLoan)}/week
              </p>
            </div>
          </>
        )}
        
        {hasNursingHomeCost && isCapped && (
          <div className="bg-green-50 p-3 rounded-md flex justify-between items-center text-green-700">
            <div className="flex items-center gap-1">
              <ShieldCheckIcon className="h-5 w-5" />
              <p className="font-medium">Capped at Nursing Home Cost</p>
              <InfoTooltip content="Your contribution is capped at the actual nursing home cost." />
            </div>
            <p className="font-semibold">{formatCurrency(formData.nursingHomeCost)}/week</p>
          </div>
        )}
        
        <div className="border-t pt-4 flex justify-between items-center">
          <div className="flex items-center gap-1">
            <EuroIcon className="h-5 w-5 text-grandpal-green" />
            <p className="font-medium">What You Actually Pay</p>
            <InfoTooltip content="Your out-of-pocket weekly payment after all adjustments." />
          </div>
          <p className="font-semibold text-grandpal-green">
            {formatCurrency(actualWeeklyPayment)}/week
          </p>
        </div>
        
        {hasNursingHomeCost && (
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <EuroIcon className="h-5 w-5 text-gray-700" />
                <p className="font-medium">Total Nursing Home Cost</p>
              </div>
              <p>{formatCurrency(formData.nursingHomeCost)}/week</p>
            </div>
            
            <div className="flex justify-between items-center text-blue-700">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5" />
                <p className="font-medium">Fair Deal State Support</p>
              </div>
              <p className="font-semibold">{formatCurrency(stateSupport)}/week</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
